import { useState } from 'react';
import { Plus, Trash2, Users } from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select } from '@/components/ui/misc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import {
  useDeleteSegment,
  usePreviewSegment,
  useSaveSegment,
  useSegmentFields,
  useSegments,
} from './api';
import type { FilterCondition, Segment } from './types';

export function SegmentsPage() {
  const { data: segments = [], isLoading } = useSegments();
  const remove = useDeleteSegment();
  const [editing, setEditing] = useState<Segment | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div>
      <PageHeader
        title="Audiences"
        description="A dynamic segment re-runs every time it is used; a static list is frozen the moment it is saved. Both matter."
        actions={
          <Can permission="marketing.segment.create">
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New segment
            </Button>
          </Can>
        }
      />

      {isLoading ? (
        <TableSkeleton columns={3} />
      ) : segments.length === 0 ? (
        <Card>
          <CardContent className="py-14 text-center">
            <Users className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No audiences yet. Build one from what you already know about your contacts.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {segments.map((segment) => (
            <Card key={segment.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="truncate font-medium">{segment.name}</h3>
                    {segment.description && (
                      <p className="truncate text-xs text-muted-foreground">
                        {segment.description}
                      </p>
                    )}
                  </div>
                  <Badge variant={segment.isDynamic ? 'default' : 'outline'}>
                    {segment.isDynamic ? 'dynamic' : 'static'}
                  </Badge>
                </div>

                <p className="tabular mt-3 text-2xl font-bold">
                  {segment.memberCount ?? '—'}
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                    contacts
                  </span>
                </p>
                {segment.lastEvaluatedAt && (
                  <p className="text-xs text-muted-foreground">
                    counted {formatDate(segment.lastEvaluatedAt)}
                  </p>
                )}

                <div className="mt-3 flex items-center gap-2 border-t pt-3">
                  <Can permission="marketing.segment.update">
                    <Button size="sm" variant="outline" onClick={() => setEditing(segment)}>
                      Edit
                    </Button>
                  </Can>
                  {segment.campaignCount > 0 && (
                    <span className="text-xs text-muted-foreground">
                      used by {segment.campaignCount} campaign
                      {segment.campaignCount === 1 ? '' : 's'}
                    </span>
                  )}
                  <Can permission="marketing.segment.delete">
                    <button
                      className="ml-auto text-muted-foreground hover:text-destructive"
                      onClick={() => remove.mutate(segment.id)}
                      aria-label="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </Can>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <SegmentDialog
          segment={editing ?? undefined}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function SegmentDialog({ segment, onClose }: { segment?: Segment; onClose: () => void }) {
  const save = useSaveSegment();
  const preview = usePreviewSegment();
  const { data: fields = [] } = useSegmentFields();

  const [name, setName] = useState(segment?.name ?? '');
  const [description, setDescription] = useState(segment?.description ?? '');
  const [isDynamic, setIsDynamic] = useState(segment?.isDynamic ?? true);
  const [match, setMatch] = useState<'ALL' | 'ANY'>(segment?.filter?.match ?? 'ALL');
  const [conditions, setConditions] = useState<FilterCondition[]>(
    segment?.filter?.conditions ?? [{ field: 'email', operator: 'contains', value: '' }],
  );
  const [error, setError] = useState<string | null>(null);

  const filter = { match, conditions: conditions.filter((condition) => condition.field) };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{segment ? `Edit ${segment.name}` : 'New audience'}</DialogTitle>
          <DialogDescription>
            Build the filter, then preview who it catches before anything is sent to them.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={name} onChange={(event) => setName(event.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
          </div>

          <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border p-3">
            <Checkbox
              checked={isDynamic}
              onCheckedChange={(value) => setIsDynamic(value === true)}
            />
            <span className="text-sm">
              Dynamic
              <span className="block text-xs text-muted-foreground">
                Re-runs the filter every time it is used. Unticked, it saves a frozen list of who
                matches right now — which is what you want for &ldquo;the people we mailed in
                March&rdquo;.
              </span>
            </span>
          </label>

          <div className="space-y-3 rounded-lg border p-3">
            <div className="flex items-center gap-2 text-sm">
              <span>Contacts matching</span>
              <Select
                className="h-8 w-auto text-xs"
                value={match}
                onChange={(event) => setMatch(event.target.value as 'ALL' | 'ANY')}
              >
                <option value="ALL">all</option>
                <option value="ANY">any</option>
              </Select>
              <span>of these:</span>
            </div>

            {conditions.map((condition, index) => (
              <div key={index} className="flex flex-wrap gap-2">
                <Select
                  className="h-9 w-auto min-w-[12rem]"
                  value={condition.field}
                  onChange={(event) => {
                    const next = [...conditions];
                    next[index] = { ...condition, field: event.target.value };
                    setConditions(next);
                  }}
                >
                  {fields.map((field) => (
                    <option key={field.key} value={field.key}>
                      {field.label}
                    </option>
                  ))}
                </Select>

                <Select
                  className="h-9 w-auto"
                  value={condition.operator ?? 'contains'}
                  onChange={(event) => {
                    const next = [...conditions];
                    next[index] = { ...condition, operator: event.target.value };
                    setConditions(next);
                  }}
                >
                  <option value="contains">contains</option>
                  <option value="equals">is</option>
                  <option value="starts_with">starts with</option>
                  <option value="not_contains">does not contain</option>
                </Select>

                <Input
                  className="h-9 min-w-[8rem] flex-1"
                  value={String(condition.value ?? '')}
                  onChange={(event) => {
                    const next = [...conditions];
                    next[index] = { ...condition, value: event.target.value };
                    setConditions(next);
                  }}
                />

                {conditions.length > 1 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConditions(conditions.filter((_, i) => i !== index))}
                  >
                    ×
                  </Button>
                )}
              </div>
            ))}

            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setConditions([...conditions, { field: fields[0]?.key ?? 'email', value: '' }])
              }
            >
              Add a condition
            </Button>
          </div>

          <Button
            variant="outline"
            className="w-full"
            disabled={preview.isPending}
            onClick={() => preview.mutate({ filter })}
          >
            {preview.isPending ? 'Counting…' : 'Preview who this catches'}
          </Button>

          {preview.data && (
            <div className="rounded-lg border p-3">
              <p className="text-sm">
                <span className="tabular text-xl font-bold">{preview.data.total}</span>
                <span className="ml-2 text-muted-foreground">contacts match</span>
              </p>
              {preview.data.withoutEmail > 0 && (
                <p className="mt-1 text-xs text-amber-600">
                  {preview.data.withoutEmail} of them have no email address, so an email campaign
                  would skip them.
                </p>
              )}
              {preview.data.sample.length > 0 && (
                <ul className="mt-2 space-y-0.5 border-t pt-2">
                  {preview.data.sample.slice(0, 8).map((contact) => (
                    <li key={contact.id} className="text-xs text-muted-foreground">
                      {contact.firstName} {contact.lastName ?? ''}
                      {contact.company && ` — ${contact.company.name}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={save.isPending || name.trim().length < 2}
            onClick={async () => {
              setError(null);
              try {
                await save.mutateAsync({
                  id: segment?.id,
                  name,
                  description: description || undefined,
                  isDynamic,
                  filter,
                });
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not save it.',
                );
              }
            }}
          >
            Save audience
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
